import {
  DocumentPlusIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  DocumentIcon,
  EyeIcon,
  CameraIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import ViewDocumentModal from "components/leads/LeadDetails/DocumentsTab/components/ViewDocumentModal";
import { downloadDocument } from "services/documentServices";
import { getDocumentUrl } from "components/leads/LeadDetails/DocumentsTab/utils/documentUtils";
import { getAttachmentPreviewUrl } from "services/documentUploadService";

const DEFAULT_ACCEPTED_FORMATS = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/msword": [".doc"],
};
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

export const AttachmentsActions = ({
  files = [],
  onChange,
  onAdd,
  onCreateDoc,
  addLabel = "Choose File",
  createLabel = "Create Document",
  className = "",
  showLabel = false,
  allowedTypes = DEFAULT_ACCEPTED_FORMATS,
  onPreviewFile,
  enablePreview = true,
  downloadKey,
  showDeviceUpload = false,
  onSelectFromStorage,
  onSelectFromDevice,
  chooseFileLabel = "Choose File",
  captureLabel = "Take Photo",
  disabled = false,
  maxFiles = 30,
  maxFileSize,
  onRemoveFile,
  scrollAfter,
  readOnly = false,
}) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activePreviewDoc, setActivePreviewDoc] = useState(null);
  const [uploadSource, setUploadSource] = useState("choose"); // 'choose' | 'capture'
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);

  const handleDownload = async (file) => {
    if (!file) return;
    const fileId =
      file.fileId ||
      file.Id ||
      file.id ||
      file.VehicleDocumentId ||
      file.IncidentDocumentId;
    const fileName = file.name || file.FileName || file.fileName || "document";

    if (fileId && downloadKey) {
      await downloadDocument(downloadKey, fileId, fileName);
      return;
    }

    if (file instanceof File || file instanceof Blob) {
      const url = URL.createObjectURL(file);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const filePathVal =
      file.FileUrl ||
      file.filePath ||
      file.FilePath ||
      file.path ||
      file.FileKey ||
      file.fileKey;
    if (filePathVal && typeof filePathVal === "string") {
      try {
        const res = await getAttachmentPreviewUrl(filePathVal);
        if (res.success && res.url) {
          try {
            const response = await fetch(res.url);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = window.document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          } catch (err) {
            console.warn("Fetch failed, falling back to new tab", err);
            const link = window.document.createElement("a");
            link.href = res.url;
            link.download = fileName;
            link.target = "_blank";
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
          }
          return;
        }
      } catch (e) {
        console.error("Failed to resolve S3 presigned URL for download", e);
      }
    }
  };

  const acceptString = allowedTypes
    ? Object.values(allowedTypes).flat().join(",")
    : undefined;

  const validateFiles = (filesToValidate) => {
    const currentCount = (files || []).length;
    const remainingSlots = maxFiles - currentCount;

    if (remainingSlots <= 0) {
      toast.error(`Maximum limit of ${maxFiles} files reached`);
      return [];
    }

    const validFiles = [];
    const invalidFiles = [];

    Array.from(filesToValidate).forEach((file) => {
      // Check for duplicates
      const isDuplicate = [...(files || []), ...validFiles].some(
        (existingFile) => {
          const existingName = String(
            existingFile.name ||
              existingFile.originalFilename ||
              existingFile.FileName ||
              existingFile.fileName ||
              existingFile.DocumentName ||
              "",
          )
            .trim()
            .toLowerCase();
          const existingSize = Number(
            existingFile.size ||
              existingFile.fileSize ||
              existingFile.FileSize ||
              0,
          );

          if (existingSize === 0) {
            return existingName === String(file.name).trim().toLowerCase();
          }
          return (
            existingName === String(file.name).trim().toLowerCase() &&
            existingSize === Number(file.size)
          );
        },
      );
      if (isDuplicate) {
        invalidFiles.push({ file, reason: "duplicate" });
        return;
      }

      // Check file size
      if (maxFileSize && file.size > maxFileSize) {
        invalidFiles.push({ file, reason: "size" });
        return;
      }

      // Check file type if allowedTypes is defined
      if (allowedTypes) {
        const fileExt = "." + file.name.split(".").pop().toLowerCase();
        const allowedExtensions = Object.values(allowedTypes)
          .flat()
          .map((ext) => ext.toLowerCase());
        const isTypeAllowed =
          allowedTypes[file.type] || allowedExtensions.includes(fileExt);

        if (!isTypeAllowed) {
          invalidFiles.push({ file, reason: "type" });
          return;
        }
      }

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ file, reason }) => {
        if (reason === "duplicate") {
          toast.error(
            `File "${file.name}" was skipped because it has already been added.`,
          );
        } else if (reason === "size") {
          toast.error(
            `File "${file.name}" was rejected: File size exceeds ${formatFileSize(maxFileSize)} limit`,
          );
        } else {
          const acceptedExts = Object.values(allowedTypes)
            .flat()
            .map((ext) => ext.replace(".", "").toUpperCase())
            .join(", ");
          toast.error(
            `File "${file.name}" was rejected: Invalid file type. Accepted formats: ${acceptedExts}`,
          );
        }
      });
    }

    if (validFiles.length > remainingSlots) {
      toast.error(
        `Maximum limit of ${maxFiles} files reached. Only ${remainingSlots} file(s) added.`,
      );
      return validFiles.slice(0, remainingSlots);
    }

    return validFiles;
  };

  const startCameraModal = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
        });
      } catch (e) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      setCameraStream(stream);
      setShowCameraModal(true);
    } catch (err) {
      console.warn(
        "Camera stream access failed, falling back to file input:",
        err,
      );
      cameraInputRef.current?.click();
    }
  };

  const stopCameraModal = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };

  useEffect(() => {
    if (showCameraModal && videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [showCameraModal, cameraStream]);

  const capturePhotoFromStream = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const fileName = `capture_${Date.now()}.jpg`;
          const file = new File([blob], fileName, { type: "image/jpeg" });
          const validFiles = validateFiles([file]);
          if (validFiles.length > 0) {
            toast.success("Photo captured successfully");
            const newFiles = [...(files || []), ...validFiles];
            onChange?.(newFiles);
          }
          stopCameraModal();
        }
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleChooseFileClick = () => {
    if (disabled) return;
    if ((files || []).length >= maxFiles) {
      toast.error(`Maximum limit of ${maxFiles} files reached`);
      return;
    }
    setUploadSource("choose");
    if (onSelectFromStorage) {
      onSelectFromStorage();
    } else if (onAdd) {
      onAdd();
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleCaptureClick = () => {
    if (disabled) return;
    if ((files || []).length >= maxFiles) {
      toast.error(`Maximum limit of ${maxFiles} files reached`);
      return;
    }
    setUploadSource("capture");
    if (onSelectFromDevice) {
      onSelectFromDevice();
    } else {
      const isMobile =
        /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        ("ontouchstart" in window && window.innerWidth < 768);

      if (isMobile && cameraInputRef.current) {
        cameraInputRef.current.click();
      } else {
        startCameraModal();
      }
    }
  };

  const handleButtonClick = () => {
    if (disabled) return;
    if ((files || []).length >= maxFiles) {
      toast.error(`Maximum limit of ${maxFiles} files reached`);
      return;
    }
    if (showDeviceUpload && uploadSource === "capture") {
      handleCaptureClick();
    } else {
      handleChooseFileClick();
    }
  };

  const handleFileChange = (e, source = "choose") => {
    if (disabled) return;
    if (e.target.files && e.target.files.length > 0 && onChange) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles = validateFiles(selectedFiles);

      if (validFiles.length > 0) {
        toast.success(
          source === "capture"
            ? "Photo captured successfully"
            : "Document added successfully",
        );
        const newFiles = [...(files || []), ...validFiles];
        onChange(newFiles);
      }
    }
    // Clear input to allow selecting the same file again if needed
    if (e.target) {
      e.target.value = null;
    }
  };

  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if ((files || []).length >= maxFiles) {
      toast.error(`Maximum limit of ${maxFiles} files reached`);
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && onChange) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = validateFiles(droppedFiles);

      if (validFiles.length > 0) {
        toast.success("Document added successfully");
        const newFiles = [...(files || []), ...validFiles];
        onChange(newFiles);
      }
    }
  };

  const handlePaste = (e) => {
    if (disabled || e.__attachmentsHandled) return;
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const rawFiles = Array.from(clipboardData.files || []);
    const items = clipboardData.items ? Array.from(clipboardData.items) : [];

    const fileList = [];
    items.forEach((item, idx) => {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          const extension = file.type?.split("/")[1]?.replace("jpeg", "jpg") || "png";
          const customName =
            file.name && file.name !== "image.png" && file.name !== "blob"
              ? file.name
              : `pasted-image-${Date.now()}-${idx + 1}.${extension}`;
          fileList.push(new File([file], customName, { type: file.type || item.type }));
        }
      }
    });

    if (fileList.length === 0 && rawFiles.length > 0) {
      fileList.push(...rawFiles);
    }

    if (fileList.length > 0 && onChange) {
      e.preventDefault();
      e.stopPropagation();
      e.__attachmentsHandled = true;
      const validFiles = validateFiles(fileList);
      if (validFiles.length > 0) {
        toast.success("Document added from clipboard");
        const newFiles = [...(files || []), ...validFiles];
        onChange(newFiles);
      }
    }
  };

  const removeFile = (indexToRemove) => {
    if (disabled) return;
    const fileToRemove = files[indexToRemove];
    if (onRemoveFile) {
      onRemoveFile(fileToRemove);
    }
    if (onChange) {
      const newFiles = files.filter((_, index) => index !== indexToRemove);
      onChange(newFiles);
    }
  };

  const IconComponent =
    showDeviceUpload && uploadSource === "capture" ? CameraIcon : FolderIcon;
  const currentCardLabel = showDeviceUpload
    ? uploadSource === "capture"
      ? captureLabel
      : chooseFileLabel
    : addLabel;
  const currentSubtext = showDeviceUpload
    ? uploadSource === "capture"
      ? "Click to capture a photo with camera"
      : "Drop files here or click to choose file"
    : "Drop files here, paste from clipboard, or click to browse";

  return (
    <div className={`flex flex-col space-y-4 ${className}`}>
      {showLabel && (
        <h4 className="dark:text-dark-400 pl-1 text-xs font-semibold text-slate-400">
          Attachments
        </h4>
      )}

      <div className="flex flex-col space-y-4">
        {/* Upload Action Card */}
        {!readOnly && (
        <>
        <button
          type="button"
          onClick={handleButtonClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPaste={handlePaste}
          className={`group relative flex shrink-0 flex-col items-center justify-center rounded-2xl border-2 border-dashed p-4 transition-all active:scale-[0.98] sm:p-5 ${
            isDragging
              ? "border-primary-500 bg-primary-50/50 dark:bg-primary-900/20"
              : "dark:border-dark-600 dark:bg-dark-800/30 hover:border-primary-500/50 hover:bg-primary-500/[0.02] border-slate-200 bg-slate-50/50"
          }`}
        >
          <div className="dark:bg-dark-700 dark:border-dark-500 mb-2 flex h-10 w-10 items-center justify-center rounded-xl border border-slate-100 bg-white shadow-xs transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 sm:h-11 sm:w-11">
            <IconComponent
              className={`h-5 w-5 sm:h-6 sm:w-6 ${isDragging ? "text-primary-600" : "text-primary-500"}`}
            />
          </div>
          <div className="text-center">
            <p className="group-hover:text-primary-600 text-sm font-bold text-slate-900 transition-colors dark:text-white">
              {currentCardLabel}
            </p>
            <p className="dark:text-dark-400 mt-0.5 text-[11px] font-medium text-slate-400">
              {currentSubtext}
            </p>
            {showDeviceUpload && (
              <div className="mt-2.5 flex items-center justify-center gap-2.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChooseFileClick();
                  }}
                  className="bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-950/40 dark:text-primary-300 border-primary-200 dark:border-primary-800 inline-flex cursor-pointer items-center gap-1.5 rounded-xl border px-3 py-1 text-xs font-bold transition-all active:scale-95"
                >
                  <FolderIcon className="h-3.5 w-3.5" />
                  Choose File
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCaptureClick();
                  }}
                  className="dark:bg-dark-700 dark:text-dark-200 dark:border-dark-600 inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 transition-all hover:bg-slate-200 active:scale-95"
                >
                  <CameraIcon className="h-3.5 w-3.5" />
                  Take Photo
                </button>
              </div>
            )}
            <div className="mt-1.5 space-x-2 text-[11px] text-gray-400 dark:text-gray-400">
              <span>
                Accepted:{" "}
                {allowedTypes
                  ? Array.from(
                      new Set(
                        Object.values(allowedTypes)
                          .flat()
                          .map((ext) => ext.replace(".", "").toUpperCase())
                      )
                    ).join(", ")
                  : "PDF, DOCX, JPG, PNG, JPEG, WEBP"}
              </span>
              {maxFileSize && (
                <>
                  <span>•</span>
                  <span>Max size: {formatFileSize(maxFileSize)}</span>
                </>
              )}
              <span>•</span>
              <span>
                Max files: {maxFiles} ({files?.length || 0}/{maxFiles})
              </span>
            </div>
          </div>
        </button>

        {/* Hidden file inputs */}
        <input
          type="file"
          multiple
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => handleFileChange(e, "choose")}
          accept={acceptString}
        />
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          ref={cameraInputRef}
          onChange={(e) => handleFileChange(e, "capture")}
        />
        </>
        )}

        {/* Selected Files List */}
        {files && files.length > 0 && (
          <div
            className={`mt-2 flex flex-col space-y-2 pr-1 ${
              scrollAfter && files.length > scrollAfter
                ? "max-h-72 overflow-y-auto"
                : ""
            }`}
          >
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="dark:border-dark-600 dark:bg-dark-700 flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3"
              >
                <div className="flex items-center space-x-3 overflow-hidden">
                  <div className="bg-primary-50 dark:bg-primary-900/20 shrink-0 rounded-lg p-2">
                    <DocumentIcon className="text-primary-500 h-5 w-5" />
                  </div>
                  <div className="flex flex-col truncate">
                    <span className="truncate text-sm font-medium text-slate-700 dark:text-gray-200">
                      {file.name ||
                        file.originalFilename ||
                        file.FileName ||
                        file.fileName ||
                        file.DocumentName ||
                        "Document"}
                    </span>
                    {(file.size || file.fileSize || file.FileSize) && (
                      <span className="text-xs text-slate-500 dark:text-gray-400">
                        {`${((file.size || file.fileSize || file.FileSize) / 1024 / 1024).toFixed(2)} MB`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {enablePreview && (
                    <button
                      type="button"
                      onClick={() => {
                        if (onPreviewFile) {
                          onPreviewFile(file, index);
                        } else {
                          setActivePreviewDoc(file);
                        }
                      }}
                      className="hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md p-1.5 text-slate-400 transition-colors"
                      title="Preview file"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                  )}
                  {!disabled && !readOnly && (
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                      title="Remove file"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {activePreviewDoc && (
          <ViewDocumentModal
            isOpen={!!activePreviewDoc}
            onClose={() => setActivePreviewDoc(null)}
            onDownload={handleDownload}
            document={activePreviewDoc}
          />
        )}

        {/* Live Camera Modal rendered at document.body level via React Portal */}
        {showCameraModal &&
          createPortal(
            <div className="animate-in fade-in fixed inset-0 z-[99999] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-md duration-200 sm:p-6">
              <div className="dark:bg-dark-800 dark:border-dark-700 my-auto flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-2xl sm:max-w-lg">
                <div className="border-gray-150 dark:border-dark-700 flex shrink-0 items-center justify-between border-b px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="bg-primary-50 dark:bg-primary-950/40 text-primary rounded-xl p-2">
                      <CameraIcon className="h-5 w-5" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 sm:text-base dark:text-white">
                      Camera Capture
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={stopCameraModal}
                    className="dark:hover:bg-dark-700 cursor-pointer rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="relative flex max-h-[60vh] min-h-[220px] flex-1 items-center justify-center overflow-hidden bg-black">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="border-gray-150 dark:border-dark-700 dark:bg-dark-850 flex shrink-0 items-center justify-between border-t bg-gray-50/50 px-5 py-3.5">
                  <button
                    type="button"
                    onClick={stopCameraModal}
                    className="cursor-pointer px-4 py-2 text-xs font-semibold text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={capturePhotoFromStream}
                    className="bg-primary-600 hover:bg-primary-700 shadow-primary-600/30 inline-flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-white shadow-lg transition-all active:scale-95"
                  >
                    <CameraIcon className="h-4 w-4" />
                    Take Photo
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default AttachmentsActions;
